import { useState, useEffect } from 'react';
import { api, invalidateCached, resolveImageUrl } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Save, Upload, X } from 'lucide-react';

export default function AdminFounder() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get('/content/founder').then(r => setContent(r.data)).catch(() => toast.error('Failed to load'));
  }, []);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/admin/upload', formData);
      setContent({ ...content, photo_url: res.data.storage_path });
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleRemovePhoto = () => {
    setContent({ ...content, photo_url: '' });
    toast.success('Founder photo removed. Save Founder Info to publish this change.');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/content/founder', content);
      invalidateCached('/content/founder');
      toast.success('Founder info saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!content) {
    return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-3 border-[#2D5BE3] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div data-testid="admin-founder-page">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-[#0F172A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Founder
        </h1>
        <p className="text-[#94A3B8] text-sm mt-1">Update the founder's photo and biography</p>
      </div>

      <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 space-y-6">
        {/* Photo */}
        <div>
          <label className="block text-sm font-medium text-[#0F172A] mb-3">Founder Photo</label>
          <div className="flex items-start gap-6">
            {content.photo_url && (
              <img
                src={resolveImageUrl(content.photo_url)}
                alt="Founder"
                className="w-32 h-40 object-cover rounded-xl"
              />
            )}
            <div>
              <div className="flex flex-wrap gap-3">
                <label
                  data-testid="founder-photo-upload-btn"
                  className={`inline-flex items-center gap-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl px-4 py-2.5 cursor-pointer hover:bg-[#EBF2FA] text-sm ${uploading ? 'opacity-60' : ''}`}
                >
                  <Upload size={16} className="text-[#475569]" />
                  {uploading ? 'Uploading...' : (content.photo_url ? 'Change Photo' : 'Upload Photo')}
                  <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} disabled={uploading} />
                </label>
                {content.photo_url && (
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="founder-photo-remove-btn"
                    onClick={handleRemovePhoto}
                    className="rounded-xl text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                  >
                    <X size={16} className="mr-2" /> Remove Photo
                  </Button>
                )}
              </div>
              <p className="text-xs text-[#94A3B8] mt-2">Recommended: 600x800px or larger portrait photo</p>
            </div>
          </div>
        </div>

        {/* Name & Title */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-1">Full Name</label>
            <Input
              data-testid="founder-name-input"
              value={content.name}
              onChange={(e) => setContent({ ...content, name: e.target.value })}
              className="rounded-xl"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-1">Title</label>
            <Input
              data-testid="founder-title-input"
              value={content.title}
              onChange={(e) => setContent({ ...content, title: e.target.value })}
              className="rounded-xl"
            />
          </div>
        </div>

        {/* Biography */}
        <div>
          <label className="block text-sm font-medium text-[#0F172A] mb-1">Biography</label>
          <Textarea
            data-testid="founder-bio-input"
            value={content.bio}
            onChange={(e) => setContent({ ...content, bio: e.target.value })}
            className="rounded-xl"
            rows={12}
          />
          <p className="text-xs text-[#94A3B8] mt-1">Use double line breaks to separate paragraphs</p>
        </div>

        <div className="flex justify-end pt-4">
          <Button data-testid="save-founder-btn" onClick={handleSave} disabled={saving}
            className="bg-[#2D5BE3] hover:bg-[#1E4FA1] text-white rounded-xl px-6">
            <Save size={16} className="mr-2" /> {saving ? 'Saving...' : 'Save Founder Info'}
          </Button>
        </div>
      </div>
    </div>
  );
}
